import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AlertCircle, Loader2, Undo2 } from 'lucide-react';
import { Button, useToast } from '@centraoneplus/ui';
import { channelService, type UndoSendSettings } from '@/services/channel.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

/** Windows offered in the picker; a value saved some other way is still shown. */
export const UNDO_WINDOW_PRESETS = [5, 10, 20, 30, 45, 60] as const;

const DEFAULT_VALUE = 'default';
const OFF_VALUE = '0';

const toValue = (seconds: number | null): string => (seconds === null ? DEFAULT_VALUE : String(seconds));
const fromValue = (value: string): number | null => (value === DEFAULT_VALUE ? null : Number(value));

function readError(err: any, fallback: string): string {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || err?.message || fallback;
}

/**
 * "Undo send" window for one email channel: how long agents get to undo a reply
 * sent from this mailbox. Saves on change; a failed save puts the old value back.
 */
export function UndoSendWindowSetting({ configId }: { configId: string }) {
  const toast = useToast();
  const selectId = useId();
  const hintId = useId();
  const [settings, setSettings] = useState<UndoSendSettings | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error' | 'unsupported'>('loading');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  // Ignore answers for a channel this component no longer shows.
  const currentId = useRef(configId);
  currentId.current = configId;

  const load = useCallback(async () => {
    const id = configId;
    setLoadState('loading');
    try {
      const result = await channelService.getUndoSendSettings(id);
      if (currentId.current !== id) return;
      setSettings(result);
      setLoadState('ready');
    } catch (err: any) {
      if (currentId.current !== id) return;
      // An email service without the setting yet: hide the control quietly.
      if (err?.response?.status === 404 && !String(err?.response?.data?.message || '').includes('Email configuration')) {
        setLoadState('unsupported');
        return;
      }
      setLoadError(readError(err, 'Could not load the undo send setting'));
      setLoadState('error');
    }
  }, [configId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleChange = async (value: string) => {
    if (!settings || saving) return;
    const seconds = fromValue(value);
    if (seconds === settings.seconds) return;
    const previous = settings;
    const id = configId;
    // Show the choice straight away; put the old one back if the save fails.
    setSettings({ ...settings, seconds });
    setSaving(true);
    try {
      const saved = await channelService.updateUndoSendSettings(id, seconds);
      if (currentId.current !== id) return;
      setSettings(saved);
      toast.success(
        'Undo send updated',
        saved.effectiveSeconds > 0
          ? `Agents can undo replies from this channel for ${saved.effectiveSeconds} seconds.`
          : 'Replies from this channel are sent straight away, without Undo.',
      );
    } catch (err: any) {
      if (currentId.current !== id) return;
      setSettings(previous);
      toast.error('Could not update undo send', readError(err, 'Please try again.'));
    } finally {
      if (currentId.current === id) setSaving(false);
    }
  };

  if (loadState === 'unsupported') return null;

  const presets: number[] = [...UNDO_WINDOW_PRESETS];
  if (settings?.seconds && !presets.includes(settings.seconds)) {
    presets.push(settings.seconds);
    presets.sort((a, b) => a - b);
  }

  let hint = '';
  if (settings) {
    if (settings.globallyDisabled) hint = 'Undo send is switched off on the server for all channels.';
    else if (settings.effectiveSeconds > 0)
      hint = `Agents see an Undo button for ${settings.effectiveSeconds} seconds after sending a reply. Changes apply to new replies.`;
    else hint = 'Replies from this channel are sent straight away, without Undo.';
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800" data-testid="undo-send-setting">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <label
          htmlFor={selectId}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          <Undo2 className="h-4 w-4" aria-hidden="true" />
          Undo send window
        </label>

        {loadState === 'loading' && (
          <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading…
          </span>
        )}

        {loadState === 'error' && (
          <span className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400" role="alert">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            {loadError}
            <Button variant="ghost" size="sm" onClick={() => void load()}>
              Retry
            </Button>
          </span>
        )}

        {loadState === 'ready' && settings && (
          <div className="flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Saving" />}
            <ThemedSelect
              id={selectId}
              value={toValue(settings.seconds)}
              disabled={saving || settings.globallyDisabled}
              aria-describedby={hintId}
              onChange={(e) => void handleChange(e.target.value)}
              className="w-full sm:w-56 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
            >
              <option value={DEFAULT_VALUE}>Default ({settings.defaultSeconds} seconds)</option>
              {presets.map((s) => (
                <option key={s} value={String(s)}>
                  {s} seconds
                </option>
              ))}
              <option value={OFF_VALUE}>Off — send immediately</option>
            </ThemedSelect>
          </div>
        )}
      </div>
      {loadState === 'ready' && (
        <p id={hintId} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
    </div>
  );
}

export default UndoSendWindowSetting;
